import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import {
  PERPLEXITY_CROSS_PROVIDER_FALLBACK_1_MODEL,
  PERPLEXITY_CROSS_PROVIDER_FALLBACK_2_MODEL,
  PERPLEXITY_DEEP_RESEARCH_MODEL,
  PERPLEXITY_DEEP_RESEARCH_FALLBACK_MODEL,
} from '../config/agenticWorkflowConfig';
import { runOpenRouterRequest } from './openRouterClient';
import { knownAccountHint } from './accountHint';

// Default to the SSOT tier-14 deep-research primary (registry-resolved via
// agenticWorkflowConfig — 2026-08-15, was a hard-coded 'sonar-deep-research').
const DEFAULT_MODEL = process.env.REACT_APP_PERPLEXITY_MODEL || PERPLEXITY_DEEP_RESEARCH_MODEL;
// SECURITY: All API calls route through Firebase proxy - no API key needed client-side
// Always use the full Firebase Hosting URL to bypass custom domain routing issues
const PERPLEXITY_PROXY_URL = 'https://tensile-tenure-453409-k3.web.app/api/perplexity';

// Direct Cloud Function URL — bypasses Firebase Hosting's 60s rewrite timeout.
// Required for non-streaming sonar-deep-research calls that take 3-5 minutes.
// The perplexityProxy function has its own 1800s timeout and CORS handling.
export const PERPLEXITY_PROXY_URL_DIRECT =
  'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net/perplexityProxy';

export interface PerplexityResearchParams {
  query: string;
  /** System prompt text. Omit when using promptId for server-side prompt resolution. */
  systemPrompt?: string;
  /** Server-side prompt identifier. When set, the proxy resolves the prompt server-side. */
  promptId?: string;
  /** Parameters for server-side prompt placeholder replacement (e.g., queryContext). */
  promptParams?: Record<string, string>;
  /**
   * D14 slice 3b (2026-08-26 ruling follow-through): SSOT task route name
   * (e.g. 'deep-research', 'deep-research-qualification'). When set, the
   * request rides perplexityProxy's mode:'task' contract — NO model and NO
   * reasoning_effort leave the browser; the server stamps the registry
   * ladder + per-rung effort and enforces the resolved model's request
   * constraints at the chokepoint. `model` then serves ONLY the marked
   * deploy-skew fallback below (an old function build has no task mode) and
   * retires with it — the inferThreadLabel doctrine.
   */
  route?: string;
  model?: string;
  signal?: AbortSignal;
  /** Optional reasoning effort for reasoning-capable models */
  reasoningEffort?: 'low' | 'medium' | 'high';
  /** Optional max tokens for response (Perplexity API supports up to 128,000) */
  maxTokens?: number;
  /** Optional search context size — controls how much web evidence is retrieved. Default is 'low'. */
  searchContextSize?: 'low' | 'medium' | 'high';
  /** Optional temperature override (0.0-1.0). If not set, uses 0.2 default. */
  temperature?: number;
  /** Called when a transient error triggers a retry attempt */
  onRetry?: (attempt: number, maxRetries: number, error: any, retryAfterMs: number) => void;
  /** Called after retry delay completes, just before the retry call executes */
  onRetryStart?: (attempt: number, maxRetries: number) => void;
  /** Optional proxy URL override. Use PERPLEXITY_PROXY_URL_DIRECT for long-running
   *  non-streaming requests that exceed Firebase Hosting's 60s rewrite timeout. */
  proxyUrl?: string;
  /** Optional timeout override in milliseconds. When set, overrides the automatic
   *  timeout detection based on model name. Use for Stage 1/2 calls that use
   *  reasoning models but should not wait 5 minutes like deep research. */
  timeout?: number;
  /** Override MAX_RETRIES for this call. Use lower values for fast-fail stages
   *  like Stage 1/2 qualification where 2 attempts is sufficient. Default: 3. */
  maxRetries?: number;
  /** Skip the fallback model entirely when primary fails. Use for stages where
   *  the fallback model (sonar-pro) can't handle the specialized prompt. Default: false. */
  skipFallback?: boolean;
  /**
   * Server-side wallet metering opt-in. When true, perplexityProxy records a
   * wallet expense (and debits olv_balance on live runs) on stream/response
   * completion. ONLY the chargeable composer deep-research stage sets this, so
   * no other perplexityProxy caller (and not the paid job path) is double-billed.
   */
  meterDeepResearch?: boolean;
  /** Optional human-readable summary label for the metered wallet expense row. */
  meterSummary?: string;
}

export interface PerplexityResearchResult {
  text: string;
  raw?: unknown;
  /** Token usage metadata for display in the UI */
  tokenUsage?: TokenUsageMetadata;
  /** Array of citation URLs returned by Perplexity alongside the content */
  citations?: string[];
}

export interface PerplexityStreamParams extends PerplexityResearchParams {
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete with text, token usage, and citations */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata, citations?: string[]) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

function extractReasoningTokens(usage: any): number | undefined {
  if (!usage) return undefined;

  // Check various field names Perplexity may use for reasoning tokens
  const candidates = [
    usage?.reasoning_tokens,
    usage?.thinking_tokens,
    usage?.compute_tokens,
    usage?.cot_tokens,
    usage?.internal_thinking_tokens,
    // Nested structures
    usage?.reasoning?.tokens,
    usage?.thinking?.tokens,
    usage?.output_tokens_details?.reasoning_tokens,
    usage?.input_tokens_details?.reasoning_tokens,
    usage?.details?.reasoning_tokens,
  ];
  const explicitValue = candidates.find((v) => typeof v === 'number');
  if (explicitValue !== undefined) return explicitValue;

  // Indirect calculation: if total_tokens > prompt_tokens + completion_tokens,
  // the difference represents internal reasoning/processing tokens
  const promptTokens = usage?.prompt_tokens;
  const completionTokens = usage?.completion_tokens;
  const totalTokens = usage?.total_tokens;

  if (
    typeof promptTokens === 'number' &&
    typeof completionTokens === 'number' &&
    typeof totalTokens === 'number'
  ) {
    const calculatedReasoning = totalTokens - promptTokens - completionTokens;
    // Only return if positive (indicates hidden internal tokens)
    if (calculatedReasoning > 0) {
      return calculatedReasoning;
    }
  }

  return undefined;
}

/**
 * Extract citation tokens from Perplexity usage data.
 * Perplexity Deep Research bills $2.00 per million citation tokens.
 */
function extractCitationTokens(usage: any): number | undefined {
  if (!usage) return undefined;

  // Check various field names Perplexity may use for citation tokens
  const candidates = [
    usage?.citation_tokens,
    usage?.citations_tokens,
    usage?.num_citation_tokens,
    usage?.citation_count,
    // Nested structures
    usage?.citations?.tokens,
    usage?.details?.citation_tokens,
    usage?.output_tokens_details?.citation_tokens,
  ];

  const value = candidates.find((v) => typeof v === 'number');
  return value !== undefined ? value : undefined;
}

/**
 * Extract search query count from Perplexity usage data.
 * Perplexity Deep Research bills $5.00 per 1,000 search queries.
 */
function extractSearchQueries(usage: any): number | undefined {
  if (!usage) return undefined;

  // Check various field names Perplexity may use for search query count
  const candidates = [
    usage?.num_search_queries,
    usage?.search_queries,
    usage?.num_searches,
    usage?.searches,
    usage?.search_count,
    // search_context_size might represent the number of search results considered
    usage?.search_context_size,
    // Nested structures
    usage?.search?.queries,
    usage?.details?.search_queries,
  ];

  const value = candidates.find((v) => typeof v === 'number');
  return value !== undefined ? value : undefined;
}

// Fallback model for when primary fails — the SSOT tier-14 Fallback 1
// (registry-resolved via agenticWorkflowConfig — 2026-08-15, was a hard-coded
// 'sonar-pro'). It is also the ONLY fallback rung that preserves the
// Perplexity citations[] contract.
const FALLBACK_MODEL = process.env.REACT_APP_PERPLEXITY_FALLBACK_MODEL || PERPLEXITY_DEEP_RESEARCH_FALLBACK_MODEL;

  // Retry configuration for transient errors (500, 502, 503, 429)
  const MAX_RETRIES = 3;
  let INITIAL_RETRY_DELAY_MS = 1000;
  
  // Backoff higher specifically for justiceisurright to manage load effectively
  if (typeof window !== 'undefined' && window.location.pathname.includes('justiceisurright')) {
    INITIAL_RETRY_DELAY_MS = 3000;
  }

// Helper to delay for exponential backoff
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Extract error message, code, and detail from proxy or API error responses.
 * Handles both nested format ({ error: { message, code } }) from Perplexity API
 * and flat format ({ error: "string", code: "...", message: "..." }) from Firebase proxy.
 */
function extractProxyError(data: any, httpStatus: number): { message: string; code: string; detail: string } {
  const errorField = data?.error;
  const isNestedError = errorField && typeof errorField === 'object';
  const isFlatStringError = typeof errorField === 'string';

  const message = (isNestedError ? errorField.message : null)
    || data?.message
    || (isFlatStringError ? errorField : null)
    || `Echo request failed (${httpStatus})`;

  const code = (isNestedError ? errorField.code : null)
    || data?.code
    || `HTTP_${httpStatus}`;

  const detail = (isNestedError ? errorField.message : null)
    || data?.message
    || (isFlatStringError ? errorField : null)
    || '';

  return { message, code, detail };
}

// ── D14 slice 3b: route classes ──────────────────────────────────────────────
// In route mode the client no longer knows which model will serve, so the
// remaining CLIENT-side composition (wall-clock timeouts, search context,
// temperature inclusion, output asks) keys on what the ROUTE means. These are
// composition defaults only — since slice 3a the SERVER enforces the resolved
// model's hard request constraints (sampling-param strip on deep-research
// rungs, the ~16K Sonar output cap) at the chokepoint, so a wrong guess here
// can no longer trigger the Feb 6 silent-downgrade landmine.
const DEEP_RESEARCH_BODY_ROUTES: ReadonlySet<string> = new Set([
  'deep-research',
  'deep-research-synthesis',
]);
const LONG_RUNNING_ROUTES: ReadonlySet<string> = new Set([
  'deep-research',
  'deep-research-synthesis',
  'deep-research-qualification',
  'deep-research-initial-findings',
]);

/**
 * Execute a deep research request against Perplexity.
 * Routes through Firebase proxy for secure API key handling.
 */
export async function runPerplexityResearch(params: PerplexityResearchParams): Promise<PerplexityResearchResult> {

  const makeBody = (model: string, useRoute: boolean) => {
    // Determine reasoning effort (LEGACY body only — in route mode the SSOT
    // rung effort rides server-side and reasoning_effort never leaves the
    // browser):
    // 1. Use explicit param if provided
    // 2. Default to 'high' for deep-research and reasoning models
    // 3. Default to 'medium' for all other models (never omit — omitting causes silent downgrade)
    const reasoningEffort: 'low' | 'medium' | 'high' =
      params.reasoningEffort ||
      (model.toLowerCase().includes('deep-research') || model.toLowerCase().includes('reasoning')
        ? 'high'
        : 'medium');

    const isDeepResearch = useRoute
      ? DEEP_RESEARCH_BODY_ROUTES.has(String(params.route))
      : model.toLowerCase().includes('deep-research');

    // Build messages: omit system message when using server-side prompt resolution
    const messages: Array<{ role: string; content: string }> = [];
    if (!params.promptId && params.systemPrompt) {
      messages.push({ role: 'system', content: params.systemPrompt });
    }
    messages.push({ role: 'user', content: params.query });

    const body: Record<string, any> = {
      // Route mode: the route name is the ONLY routing input — the server
      // stamps model/ladder/effort. Legacy mode: the model id (deploy-skew
      // fallback; retires with it).
      ...(useRoute ? { mode: 'task', route: params.route } : { model }),
      messages,
      // sonar-deep-research does NOT support temperature, top_p, top_k, or any sampling
      // parameters. Sending them triggers the Feb 6 2026 silent downgrade to medium reasoning.
      // Only include temperature for non-deep-research models (server re-enforces in route mode).
      ...(isDeepResearch ? {} : { temperature: params.temperature ?? 0.2 }),
    };

    // Server-side prompt resolution: send promptId + params instead of raw prompt
    if (params.promptId) {
      body.promptId = params.promptId;
      if (params.promptParams) {
        body.promptParams = params.promptParams;
      }
    }

    // Wallet-metering opt-in: only the chargeable composer deep-research stage
    // sets this, gating proxy-side expense recording to prevent double-billing.
    if (params.meterDeepResearch) {
      body.meterDeepResearch = true;
      if (params.meterSummary) body.meterSummary = params.meterSummary;
    }

    // LEGACY body only: always include reasoning_effort — omitting it after
    // Perplexity's Feb 6 2026 change causes silent default to 'medium'. In
    // route mode the SSOT rung effort rides the server rewrite instead.
    if (!useRoute) {
      body.reasoning_effort = reasoningEffort;
    }

    if (params.maxTokens) {
      // sonar-deep-research supports up to 128K output tokens.
      // Standard Sonar models (reasoning-pro, pro, etc.) have a practical limit of ~16K.
      // Cap to prevent 500 errors from exceeding model capabilities.
      body.max_tokens = isDeepResearch ? params.maxTokens : Math.min(params.maxTokens, 16000);
    }

    // Always include web_search_options — default to 'high' for deep-research, 'low' for others.
    body.web_search_options = {
      search_context_size: params.searchContextSize ||
        (isDeepResearch ? 'high' : 'low'),
    };

    // Diagnostic: log exact payload being sent (excluding message content for privacy)
    console.log('[Perplexity] Request payload:', JSON.stringify({
      model: body.model,
      reasoning_effort: body.reasoning_effort,
      max_tokens: body.max_tokens,
      temperature: body.temperature ?? 'OMITTED (deep-research)',
      web_search_options: body.web_search_options,
      messagesCount: body.messages?.length,
    }));

    return body;
  };

  const primaryModel = params.model || DEFAULT_MODEL;
  // NOTE: To revert to deep research fallback, change this to:
  // const fallbackModel = primaryModel === 'sonar-reasoning-pro' ? 'sonar-deep-research' : FALLBACK_MODEL;
  const fallbackModel = FALLBACK_MODEL;

  async function callOnce(model: string, runId: string, useRoute: boolean) {
    // Record start time for response time calculation
    const startTime = Date.now();

    // Create timeout - deep research and reasoning models need longer to respond
    // sonar-deep-research: internal search phase takes 3-5 min
    // sonar-reasoning-pro with high reasoning effort: routinely takes 60-120s
    // Callers can override via params.timeout for stage-specific control
    const isLongRunningModel = useRoute
      ? LONG_RUNNING_ROUTES.has(String(params.route))
      : (model.toLowerCase().includes('deep-research') || model.toLowerCase().includes('reasoning'));
    const PERPLEXITY_TIMEOUT_MS = params.timeout ?? (isLongRunningModel ? 300000 : 60000);
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), PERPLEXITY_TIMEOUT_MS);

    // If external signal aborts, also abort our timeout controller
    if (params.signal) {
      params.signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        timeoutController.abort();
      });
    }

    try {
      const accountHint = knownAccountHint();
      const response = await fetch(params.proxyUrl || PERPLEXITY_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
          ...(useRoute ? { 'X-Prest0n-Task': String(params.route) } : {}),
        },
        body: JSON.stringify(makeBody(model, useRoute)),
        signal: timeoutController.signal,
      });
      clearTimeout(timeoutId);

    // Capture raw body for debugging (cloned so we don't consume the main stream)
    let rawText = '';
    try {
      const clone = response.clone();
      rawText = await clone.text();
    } catch {
      rawText = '';
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      // Handle both nested (Perplexity API) and flat (Firebase proxy) error formats
      const { message, code, detail } = extractProxyError(data, response.status);
      const rawError = (data as any)?.error || {};
      const rawErrorPreview = (() => {
        try {
          const serialized = JSON.stringify(rawError);
          return serialized.length > 500 ? `${serialized.slice(0, 500)}...` : serialized;
        } catch {
          return '';
        }
      })();
      const rawBodyPreview = rawText ? (rawText.length > 500 ? `${rawText.slice(0, 500)}...` : rawText) : '';
      const err: any = new Error(message);
      err.status = response.status;
      err.model = model;
      err.code = code;
      err.detail = detail;
      throw err;
    }

    const text = (data as any)?.choices?.[0]?.message?.content?.trim()
      || (data as any)?.output?.content?.text?.trim()
      || '';
    const finishReason = (data as any)?.choices?.[0]?.finish_reason;
    if (!text) {
      const err: any = new Error('Echo returned an empty response.');
      err.status = response.status;
      err.model = model;
      throw err;
    }

    // Extract citation URLs from Perplexity response (top-level citations array)
    const citations: string[] = Array.isArray((data as any)?.citations)
      ? (data as any).citations
      : [];

    if (citations.length === 0) {
      console.warn('[Perplexity] WARNING: Empty citations array — possible grounding failure for model:', model);
    }

    // Calculate response time
    const responseTimeMs = Date.now() - startTime;

    // Extract token usage from response
    // Perplexity may return reasoning/citation/search tokens under various field names
    const usage = (data as any)?.usage;
    const reasoningTokens = extractReasoningTokens(usage);
    const citationTokens = extractCitationTokens(usage);
    // Perplexity returns search queries (count of queries, not tokens) - billed at $5/1K queries
    const searchQueries = extractSearchQueries(usage);
    // Extract upstream cost from OpenRouter's cost_details (authoritative, includes search fees)
    const upstreamCostUsd = usage?.cost_details?.upstream_inference_cost
      || (typeof usage?.cost === 'number' ? usage.cost : undefined);
    const tokenUsage: TokenUsageMetadata = {
      inputTokens: usage?.prompt_tokens,
      outputTokens: usage?.completion_tokens,
      reasoningTokens,
      citationTokens,
      searchQueries,
      totalTokens: usage?.total_tokens,
      model,
      provider: 'perplexity',
      responseTimeMs,
      timestamp: new Date(),
      finishReason: finishReason || undefined,
      upstreamCostUsd: typeof upstreamCostUsd === 'number' ? upstreamCostUsd : undefined,
    };

    // Log finish_reason for diagnostics
    if (finishReason) {
      console.log(`[Perplexity] finish_reason: "${finishReason}", completion_tokens: ${usage?.completion_tokens}, max_tokens_sent: ${params.maxTokens || 'not set'}`);
    }

    // Log raw usage object to diagnose token extraction (especially reasoning tokens)
    if (usage) {
      console.log('[Perplexity] Raw usage object:', JSON.stringify(usage));
      console.log('[Perplexity] Extracted tokens:', { reasoningTokens, citationTokens, searchQueries });
    }

    return { text, raw: data, tokenUsage, citations } as PerplexityResearchResult;
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      // Re-throw with additional context for timeout errors
      if (fetchError?.name === 'AbortError') {
        const timeoutSec = Math.round(PERPLEXITY_TIMEOUT_MS / 1000);
        const err: any = new Error(`Request timed out after ${timeoutSec} seconds`);
        err.status = 408;
        err.model = model;
        err.code = 'TIMEOUT';
        throw err;
      }
      throw fetchError;
    }
  }

  const primaryRunId = 'deep-research-primary';
  const fallbackRunId = 'deep-research-fallback';

  // Auth error codes that should never be retried (not transient — will always fail)
  const AUTH_ERROR_CODES = ['API_KEY_NOT_CONFIGURED', 'PERPLEXITY_AUTH_FAILED'];
  const isAuthError = (err: any): boolean => AUTH_ERROR_CODES.includes(err?.code);

  // Helper to check if error is retryable (transient server errors + empty responses)
  const isRetryableError = (err: any): boolean => {
    // Never retry auth/config errors — they're permanent until the API key is fixed
    if (isAuthError(err)) return false;
    const status = err?.status;
    // Status-less browser network failures (e.g. `TypeError: Failed to fetch` on a
    // transient blip during the long-running deep-research fetch) carry NO status.
    // Without this, a single network hiccup was thrown on the first attempt and the
    // configured maxRetries (e.g. 4 on justiceisurright) never fired — silently
    // killing the whole deep-research run. Treat these as transient/retryable.
    const msg = String(err?.message || '').toLowerCase();
    const isNetwork = (err?.name === 'TypeError' && !status)
      || /failed to fetch|networkerror|network error|fetch failed|load failed|econnreset|econnrefused|etimedout|socket hang up/.test(msg);
    // 500/502/503: transient server errors
    // 504: gateway timeout (Firebase Hosting 60s limit)
    // 429: rate limit (retry after backoff)
    // 408: request timeout (Perplexity API or client-side timeout — transient)
    // 200: empty response from API (acknowledged but no content — transient)
    return isNetwork || status === 500 || status === 502 || status === 503 || status === 504 || status === 429 || status === 408 || status === 200;
  };

  // Call with retry logic for transient errors
  const effectiveMaxRetries = params.maxRetries ?? MAX_RETRIES;
  async function callWithRetry(model: string, runId: string, useRoute: boolean): Promise<PerplexityResearchResult> {
    let lastError: any = null;

    for (let attempt = 0; attempt < effectiveMaxRetries; attempt++) {
      if (attempt > 0) {
        const backoffMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
        console.log(`[Perplexity] Retry ${attempt}/${effectiveMaxRetries} after ${backoffMs}ms for ${model}`);
        params.onRetry?.(attempt, effectiveMaxRetries, lastError, backoffMs);
        await delay(backoffMs);
        // Notify consumer that retry delay is complete and the actual request is starting
        params.onRetryStart?.(attempt, effectiveMaxRetries);
      }

      try {
        return await callOnce(model, `${runId}-attempt-${attempt}`, useRoute);
      } catch (err: any) {
        lastError = err;

        // Only retry on transient errors
        if (!isRetryableError(err)) {
          throw err;
        }

        console.log(`[Perplexity] Transient error (${err?.status}), will retry: ${err?.message}`);

        // If this was the last attempt, throw
        if (attempt === effectiveMaxRetries - 1) {
          throw err;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  }

  // Fallback classification (server errors + network failures). Recomputed if
  // the deploy-skew retry below substitutes its own error for the ladder.
  const classifyForFallback = (err: any) => {
    const message = String(err?.message || '').toLowerCase();
    const isServerError = err?.status >= 500;
    const isNetworkError = message.includes('network error') || message.includes('fetch failed') ||
      message.includes('failed to fetch') || message.includes('load failed') ||
      message.includes('networkerror') || message.includes('the internet connection') ||
      message.includes('econnrefused') || message.includes('econnreset') ||
      message.includes('etimedout') || message.includes('socket hang up');
    return { message, isServerError, isNetworkError };
  };

  const routeMode = Boolean(params.route);
  try {
    return await callWithRetry(primaryModel, primaryRunId, routeMode);
  } catch (caught: any) {
    let err: any = caught;
    // Never fallback for auth/config errors — the fallback model uses the same API key
    // and will fail identically. Fail fast instead of making 3 more useless requests.
    if (isAuthError(err)) {
      console.error(`[Perplexity] Auth error (${err.code}), skipping fallback — API key issue`);
      throw err;
    }

    let { message, isServerError, isNetworkError } = classifyForFallback(err);

    // ── DEPLOY-SKEW FALLBACK (delete next release — inferThreadLabel
    // doctrine): a route-mode failure retries ONCE with the legacy
    // primary-model body — an old function build has no task mode (its 400
    // on the modeless body is included alongside server/network errors for
    // exactly that reason). Post-deploy this fires only when the whole
    // plane is failing, where the legacy body fares no better. Runs BEFORE
    // the skipFallback throw on purpose: skipFallback declines the sonar-pro
    // MODEL fallback; deploy skew is transport repair, and stages 1/2 (which
    // set skipFallback) must survive the deploy window too. If the legacy
    // body also fails, its error re-enters the ladder below.
    if (routeMode && (isServerError || isNetworkError || err?.status === 400)) {
      console.warn(`[Perplexity] route-mode call failed (${err?.status ?? 'network'}); using deploy-skew fallback body (${primaryModel})`);
      try {
        return await callWithRetry(primaryModel, `${fallbackRunId}-legacy`, false);
      } catch (legacyErr: any) {
        if (isAuthError(legacyErr)) {
          console.error(`[Perplexity] Auth error (${legacyErr.code}) on deploy-skew fallback — API key issue`);
          throw legacyErr;
        }
        err = legacyErr;
        ({ message, isServerError, isNetworkError } = classifyForFallback(err));
      }
    }

    // Caller requested no fallback (e.g., Stage 1/2 where sonar-pro can't handle the prompt)
    if (params.skipFallback) {
      console.error(`[Perplexity] Primary model ${primaryModel} failed, skipFallback=true — not falling back`);
      throw err;
    }

    // Fallback to alternative model on server errors or network failures.
    // This provides resilience when the primary model (e.g., sonar-reasoning-pro)
    // has transient 500 errors — falls back to sonar-pro for continued operation.
    // In route mode the server already descended the full SSOT ladder via the
    // stamped models array, so the client model-fallback stays legacy-only.
    const shouldFallback = (isServerError || isNetworkError) && fallbackModel && fallbackModel !== primaryModel;

    if (!routeMode && shouldFallback) {
      console.log(`[Perplexity] Primary model ${primaryModel} failed after retries, falling back to ${fallbackModel}`);
      return await callWithRetry(fallbackModel, fallbackRunId, false);
    }

    // -----------------------------------------------------------------------
    // CROSS-PROVIDER FALLBACK: Try OpenRouter as last resort
    // Only triggers after ALL Perplexity retries + model fallback have failed.
    // Does NOT trigger on auth errors (skipFallback already handles those above).
    // Reuses isServerError/isNetworkError already computed above.
    // -----------------------------------------------------------------------
    const isTimeoutError = message.includes('timed out') || message.includes('timeout');
    const isServerOrNetworkError = isServerError || isNetworkError || isTimeoutError;

    if (isServerOrNetworkError) {
      console.log(`[Perplexity] All Perplexity models failed — trying cross-provider fallbacks`);
      console.log(`[Perplexity] Cross-provider chain (SSOT tier-14 rungs 3-4): ${PERPLEXITY_CROSS_PROVIDER_FALLBACK_1_MODEL} → ${PERPLEXITY_CROSS_PROVIDER_FALLBACK_2_MODEL} (both with web search plugin)`);

      // Cross-provider fallback chain (2026-08-15 misroute fix): descend the SSOT
      // tier-14 ladder's own rungs 3-4 (gemini-pro, then grok) — the old chain's
      // Claude Opus rung was not a tier-14 member and, with no web grounding,
      // shipped reports whose bracket cites had no backing sources. Tier 14 is
      // webSearch:true, so BOTH rungs carry the OpenRouter web plugin.
      const crossProviderModels = [
        // gemini-pro rung: 'high' is the Google ceiling per the SSOT rung spec.
        { model: PERPLEXITY_CROSS_PROVIDER_FALLBACK_1_MODEL, reasoning: { effort: 'high' as const } as { effort: 'high' | 'xhigh' }, plugins: [{ id: 'web' }] as undefined | Array<{ id: string }> },
        // grok rung: SSOT tier-14 pins thinking:xhigh (grok-4.6 widened the xAI enum).
        { model: PERPLEXITY_CROSS_PROVIDER_FALLBACK_2_MODEL, reasoning: { effort: 'xhigh' as const } as { effort: 'high' | 'xhigh' }, plugins: [{ id: 'web' }] },
      ];
      let lastCrossProviderError: any = null;

      for (const { model: orModel, reasoning, plugins } of crossProviderModels) {
        try {
          console.log(`[Perplexity] Trying cross-provider: ${orModel}${plugins ? ' (with web search)' : ''}`);

          // Build messages (same structure as Perplexity)
          const orMessages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [];
          if (!params.promptId && params.systemPrompt) {
            orMessages.push({ role: 'system', content: params.systemPrompt });
          }
          orMessages.push({ role: 'user', content: params.query });

          const orResult = await runOpenRouterRequest({
            model: orModel,
            messages: orMessages,
            maxTokens: params.maxTokens ? Math.min(params.maxTokens, 32000) : 16000,
            temperature: params.temperature ?? 0.2,
            signal: params.signal,
            reasoning,
            plugins,
          });

          console.log(`[Perplexity] Cross-provider fallback succeeded with: ${orModel}`);
          return {
            text: orResult.text,
            raw: orResult.raw,
            tokenUsage: orResult.tokenUsage,
            citations: [], // Cross-provider doesn't provide Perplexity-style citations
          };
        } catch (orErr: any) {
          console.error(`[Perplexity] Cross-provider ${orModel} failed: ${orErr?.message}`);
          lastCrossProviderError = orErr;
        }
      }

      console.error(`[Perplexity] All cross-provider fallbacks also failed`);
      throw lastCrossProviderError || err;
    }

    // Surface error with codename instead of raw model ID
    const codename = primaryModel === 'sonar-deep-research' ? 'Echo Deep Research'
      : primaryModel === 'sonar-reasoning-pro' ? 'Echo Reasoning Pro'
      : primaryModel === 'sonar-pro' ? 'Echo Pro'
      : primaryModel;
    const statusInfo = err?.status ? ` (${err.status})` : '';
    console.error(`[Perplexity] ${codename} failed after ${effectiveMaxRetries} retries${statusInfo}: ${err?.message}`);
    throw err;
  }
}

/**
 * Execute a streaming deep research request against Perplexity.
 * This allows real-time display of results as they are generated.
 * Routes through Firebase proxy for secure API key handling.
 */
export async function runPerplexityResearchStream(params: PerplexityStreamParams): Promise<PerplexityResearchResult> {

  const model = params.model || DEFAULT_MODEL;
  // D14 slice 3b: route mode — the route name is the only routing input; the
  // server stamps model/ladder/effort and enforces the resolved model's
  // request constraints. `model` above then serves only the marked
  // deploy-skew fallback below (delete next release).
  const streamRouteMode = Boolean(params.route);
  // Determine reasoning effort (LEGACY body only — route mode never sends it):
  // 1. Use explicit param if provided
  // 2. Default to 'high' for deep-research and reasoning models
  // 3. Default to 'medium' for all other models (never omit — omitting causes silent downgrade)
  const reasoningEffort: 'low' | 'medium' | 'high' =
    params.reasoningEffort ||
    (model.toLowerCase().includes('deep-research') || model.toLowerCase().includes('reasoning')
      ? 'high'
      : 'medium');

  const isDeepResearch = streamRouteMode
    ? DEEP_RESEARCH_BODY_ROUTES.has(String(params.route))
    : model.toLowerCase().includes('deep-research');

  // Build messages: omit system message when using server-side prompt resolution
  const streamMessages: Array<{ role: string; content: string }> = [];
  if (!params.promptId && params.systemPrompt) {
    streamMessages.push({ role: 'system', content: params.systemPrompt });
  }
  streamMessages.push({ role: 'user', content: params.query });

  const body: Record<string, any> = {
    ...(streamRouteMode ? { mode: 'task', route: params.route } : { model }),
    messages: streamMessages,
    stream: true,
    // CRITICAL: Request usage data in streaming responses.
    // Without this, OpenRouter (following OpenAI convention) does NOT include token counts
    // in SSE chunks, causing all streaming requests to record $0 usage.
    stream_options: { include_usage: true },
    // sonar-deep-research does NOT support temperature, top_p, top_k, or any sampling
    // parameters. Sending them triggers the Feb 6 2026 silent downgrade to medium reasoning.
    // Only include temperature for non-deep-research models.
    ...(isDeepResearch ? {} : { temperature: params.temperature ?? 0.2 }),
  };

  // Server-side prompt resolution: send promptId + params instead of raw prompt
  if (params.promptId) {
    body.promptId = params.promptId;
    if (params.promptParams) {
      body.promptParams = params.promptParams;
    }
  }

  // Wallet-metering opt-in: only the chargeable composer deep-research stage
  // sets this, gating proxy-side expense recording to prevent double-billing.
  if (params.meterDeepResearch) {
    body.meterDeepResearch = true;
    if (params.meterSummary) body.meterSummary = params.meterSummary;
  }

  // LEGACY body only: always include reasoning_effort — omitting it after
  // Perplexity's Feb 6 2026 change causes silent default to 'medium'. In
  // route mode the SSOT rung effort rides the server rewrite instead.
  if (!streamRouteMode) {
    body.reasoning_effort = reasoningEffort;
  }

  if (params.maxTokens) {
    // sonar-deep-research supports up to 128K output tokens.
    // Standard Sonar models (reasoning-pro, pro, etc.) have a practical limit of ~16K.
    // Cap to prevent 500 errors from exceeding model capabilities.
    body.max_tokens = isDeepResearch ? params.maxTokens : Math.min(params.maxTokens, 16000);
  }

  // Always include web_search_options — default to 'high' for deep-research, 'low' for others.
  body.web_search_options = {
    search_context_size: params.searchContextSize ||
      (isDeepResearch ? 'high' : 'low'),
  };

  // Diagnostic: log exact payload being sent (excluding message content for privacy)
  console.log('[Perplexity Stream] Request payload:', JSON.stringify({
    model: body.model,
    stream: body.stream,
    reasoning_effort: body.reasoning_effort,
    max_tokens: body.max_tokens,
    temperature: body.temperature ?? 'OMITTED (deep-research)',
    web_search_options: body.web_search_options,
    messagesCount: body.messages?.length,
  }));

  // Record start time for response time calculation
  const startTime = Date.now();

  // Helper to check if error is retryable (transient server errors)
  const isRetryableStatus = (status: number): boolean => {
    return status === 500 || status === 502 || status === 503 || status === 504 || status === 429 || status === 408;
  };

  // Retry logic for initial connection
  let response: Response | null = null;
  let lastError: any = null;

  const streamMaxRetries = params.maxRetries ?? MAX_RETRIES;
  for (let attempt = 0; attempt < streamMaxRetries; attempt++) {
    if (attempt > 0) {
      const backoffMs = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
      console.log(`[Perplexity Stream] Retry ${attempt}/${streamMaxRetries} after ${backoffMs}ms for ${model}`);
      params.onRetry?.(attempt, streamMaxRetries, lastError, backoffMs);
      await delay(backoffMs);
    }

    // Create timeout controller - deep research and reasoning models need longer to start
    // Callers can override via params.timeout for stage-specific control
    const isLongRunningModel = streamRouteMode
      ? LONG_RUNNING_ROUTES.has(String(params.route))
      : (model.toLowerCase().includes('deep-research') || model.toLowerCase().includes('reasoning'));
    const PERPLEXITY_STREAM_TIMEOUT_MS = params.timeout ?? (isLongRunningModel ? 300000 : 60000);
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), PERPLEXITY_STREAM_TIMEOUT_MS);

    // If external signal aborts, also abort our timeout controller
    if (params.signal) {
      params.signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        timeoutController.abort();
      });
    }

    try {
      const accountHint = knownAccountHint();
      response = await fetch(params.proxyUrl || PERPLEXITY_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
          ...(streamRouteMode ? { 'X-Prest0n-Task': String(params.route) } : {}),
        },
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });
      clearTimeout(timeoutId);

      // If response is OK or non-retryable error, break out of retry loop
      if (response.ok || !isRetryableStatus(response.status)) {
        break;
      }

      // For retryable errors, capture error info and continue to next attempt
      console.log(`[Perplexity Stream] Transient error (${response.status}), will retry`);
      params.onRetry?.(attempt + 1, streamMaxRetries, { status: response.status, message: 'Transient server error' }, INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt));

      if (attempt === streamMaxRetries - 1) {
        // Last attempt, will handle error below
        break;
      }
    } catch (fetchErr: any) {
      lastError = fetchErr;
      console.log(`[Perplexity Stream] Fetch error: ${fetchErr?.message}, will retry`);

      if (attempt === streamMaxRetries - 1) {
        params.onError?.(fetchErr);
        throw fetchErr;
      }
    }
  }

  // ── DEPLOY-SKEW FALLBACK (delete next release — inferThreadLabel doctrine):
  // a route-mode stream that never connected, or answered non-OK (an old
  // function build 400s the modeless body), retries ONCE with the legacy
  // model body on a single connection attempt.
  if (streamRouteMode && (!response || !response.ok)) {
    console.warn(`[Perplexity Stream] route-mode connect failed (${response ? response.status : 'network'}); using deploy-skew fallback body (${model})`);
    const legacyBody: Record<string, any> = { ...body, model };
    delete legacyBody.mode;
    delete legacyBody.route;
    legacyBody.reasoning_effort = reasoningEffort;
    // Same connect-timeout shape as the attempts above — a hung legacy door
    // must not wedge the stream for the life of the tab.
    const legacyTimeoutController = new AbortController();
    const legacyTimeoutId = setTimeout(
      () => legacyTimeoutController.abort(),
      params.timeout ?? 300000,
    );
    if (params.signal) {
      params.signal.addEventListener('abort', () => {
        clearTimeout(legacyTimeoutId);
        legacyTimeoutController.abort();
      });
    }
    try {
      const accountHint = knownAccountHint();
      response = await fetch(params.proxyUrl || PERPLEXITY_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
        },
        body: JSON.stringify(legacyBody),
        signal: legacyTimeoutController.signal,
      });
    } catch (legacyErr: any) {
      lastError = legacyErr;
      response = null;
    } finally {
      clearTimeout(legacyTimeoutId);
    }
  }

  if (!response) {
    const err = lastError || new Error('Failed to connect to Echo after retries');
    params.onError?.(err);
    throw err;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Handle both nested (Perplexity API) and flat (Firebase proxy) error formats
    const { message, code, detail } = extractProxyError(errorData, response.status);
    const err: any = new Error(message);
    err.status = response.status;
    err.model = model;
    err.code = code;
    err.detail = detail;
    params.onError?.(err);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for streaming');
    params.onError?.(err);
    throw err;
  }

  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  let accumulatedCitations: string[] = [];
  let lastFinishReason: string | null = null;
  let lastUsageData: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
    reasoning_tokens?: number;
    thinking_tokens?: number;
    search_context_size?: number;
    num_search_queries?: number;
    reasoning?: { tokens?: number };
    thinking?: { tokens?: number };
    citation_tokens?: number;
  } | null = null;

  // Timeout for each stream chunk read - prevents indefinite hangs on network issues
  // Deep research models can have long reasoning pauses (30+ seconds), so use extended timeout
  const isDeepResearchModel = model.toLowerCase().includes('deep-research') || model.toLowerCase().includes('reasoning');
  const STREAM_CHUNK_TIMEOUT_MS = isDeepResearchModel ? 120000 : 30000; // 2 min for deep research, 30s for others

  try {
    while (true) {
      // Check abort signal before each read to allow cancellation
      if (params.signal?.aborted) {
        console.log('[Perplexity Stream] Abort signal detected - stopping stream');
        break;
      }

      // Add timeout to each read operation to prevent indefinite hangs
      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Stream chunk timeout - no data received for 30 seconds')), STREAM_CHUNK_TIMEOUT_MS);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[Perplexity Stream] Stream chunk timeout - aborting read loop');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed === 'data: [DONE]') continue;
        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6); // Remove 'data: ' prefix
          const parsed = JSON.parse(jsonStr);
          const delta = parsed?.choices?.[0]?.delta?.content
            || parsed?.output?.content?.text;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }
          // Capture finish_reason (null during streaming, 'stop' or 'length' in final chunk)
          const finishReason = parsed?.choices?.[0]?.finish_reason
            || parsed?.stop_reason;
          if (finishReason) {
            lastFinishReason = finishReason;
          }
          // Capture usage data if present (usually in final chunk)
          if (parsed?.usage) {
            lastUsageData = parsed.usage;
          }
          // Capture citation URLs from any of the three locations OpenRouter/Perplexity
          // may use (top-level, choices[0].message.citations on final chunk, or
          // choices[0].delta.citations in deltas). BUG-FH-4 (2026-05-18): some
          // OpenRouter routes only emit citations under choices[0].message.citations
          // on the final SSE event; checking only the top-level field caused the
          // homepage deep-research path to drop every citation and ship a report
          // body full of [N] markers with a "No external sources were cited" footer.
          // The executeWidgetResearchStage2 path in functions/src/index.ts already
          // checks all three locations for backup providers; mirroring that here for
          // the primary streaming path.
          {
            const chunkCitations: Array<string | null | undefined> = [
              ...(Array.isArray(parsed?.citations) ? parsed.citations : []),
              ...(Array.isArray(parsed?.choices?.[0]?.message?.citations) ? parsed.choices[0].message.citations : []),
              ...(Array.isArray(parsed?.choices?.[0]?.delta?.citations) ? parsed.choices[0].delta.citations : []),
            ];
            if (chunkCitations.length > 0) {
              const seenUrls = new Set(accumulatedCitations);
              for (const raw of chunkCitations) {
                if (typeof raw === 'string' && raw.startsWith('http') && !seenUrls.has(raw)) {
                  seenUrls.add(raw);
                  accumulatedCitations.push(raw);
                }
              }
            }
          }
        } catch {
          // Ignore malformed JSON chunks
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim() && buffer.trim() !== 'data: [DONE]') {
      const trimmed = buffer.trim();
      if (trimmed.startsWith('data: ')) {
        try {
          const jsonStr = trimmed.slice(6);
          const parsed = JSON.parse(jsonStr);
          const delta = parsed?.choices?.[0]?.delta?.content
            || parsed?.output?.content?.text;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }
          const bufFinishReason = parsed?.choices?.[0]?.finish_reason
            || parsed?.stop_reason;
          if (bufFinishReason) {
            lastFinishReason = bufFinishReason;
          }
          if (parsed?.usage) {
            lastUsageData = parsed.usage;
          }
          // Same multi-location citation capture as the main loop above (BUG-FH-4).
          {
            const tailCitations: Array<string | null | undefined> = [
              ...(Array.isArray(parsed?.citations) ? parsed.citations : []),
              ...(Array.isArray(parsed?.choices?.[0]?.message?.citations) ? parsed.choices[0].message.citations : []),
              ...(Array.isArray(parsed?.choices?.[0]?.delta?.citations) ? parsed.choices[0].delta.citations : []),
            ];
            if (tailCitations.length > 0) {
              const seenTailUrls = new Set(accumulatedCitations);
              for (const raw of tailCitations) {
                if (typeof raw === 'string' && raw.startsWith('http') && !seenTailUrls.has(raw)) {
                  seenTailUrls.add(raw);
                  accumulatedCitations.push(raw);
                }
              }
            }
          }
        } catch {
          // Ignore
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  // FALLBACK: If OpenRouter didn't return usage data in the stream (e.g., provider doesn't
  // support stream_options.include_usage), estimate token counts from the accumulated text.
  // This ensures we never record $0 for a successful deep research that produced content.
  if (!lastUsageData && finalText.length > 0) {
    console.warn('[Perplexity Stream] No usage data received in stream — estimating from content length');
    // Rough estimate: ~4 characters per token for English text
    const estimatedOutputTokens = Math.ceil(finalText.length / 4);
    // Input tokens are harder to estimate; use a conservative ratio based on typical deep research
    // (system prompt + user query + context is usually 2000-8000 tokens)
    const estimatedInputTokens = Math.ceil(estimatedOutputTokens * 0.3);
    lastUsageData = {
      prompt_tokens: estimatedInputTokens,
      completion_tokens: estimatedOutputTokens,
      total_tokens: estimatedInputTokens + estimatedOutputTokens,
    };
    console.log('[Perplexity Stream] Estimated usage:', {
      prompt_tokens: estimatedInputTokens,
      completion_tokens: estimatedOutputTokens,
      contentLength: finalText.length,
    });
  }

  // Build token usage metadata (check for reasoning/citation/search tokens under various field names)
  let reasoningTokens = extractReasoningTokens(lastUsageData);

  // Perplexity reasoning models (sonar-reasoning-pro) do internal chain-of-thought but
  // do NOT expose reasoning_tokens through the API — completion_tokens only covers the
  // visible output. Estimate reasoning tokens from response time:
  //   total time - estimated output generation time = estimated reasoning time
  //   reasoning tokens ≈ reasoning time × reasoning rate
  // Calibrated constants for sonar-reasoning-pro via OpenRouter:
  //   output generation rate: ~80 tokens/sec
  //   reasoning token rate:   ~100 tokens/sec
  if (reasoningTokens === undefined) {
    const isReasoningModel = model.toLowerCase().includes('reasoning');
    if (isReasoningModel && responseTimeMs > 0) {
      const outputTokens = lastUsageData?.completion_tokens || Math.ceil(finalText.length / 4);
      const OUTPUT_RATE_TPS = 80;    // sonar-reasoning-pro output generation speed
      const REASONING_RATE_TPS = 100; // internal chain-of-thought generation speed
      const estimatedOutputMs = (outputTokens / OUTPUT_RATE_TPS) * 1000;
      const estimatedReasoningMs = Math.max(0, responseTimeMs - estimatedOutputMs);
      const estimatedReasoningTokens = Math.round((estimatedReasoningMs / 1000) * REASONING_RATE_TPS);
      // Only attribute if meaningful (at least 100 reasoning tokens to avoid noise)
      if (estimatedReasoningTokens >= 100) {
        reasoningTokens = estimatedReasoningTokens;
        console.log('[Perplexity Stream] Estimated reasoning tokens from response time:', {
          responseTimeMs,
          outputTokens,
          estimatedOutputMs: Math.round(estimatedOutputMs),
          estimatedReasoningMs: Math.round(estimatedReasoningMs),
          reasoningTokens: estimatedReasoningTokens,
          model,
        });
      }
    }
  }

  const citationTokens = extractCitationTokens(lastUsageData);
  // Perplexity returns search queries (count of queries, not tokens) - billed at $5/1K queries
  const searchQueries = extractSearchQueries(lastUsageData);
  // Extract upstream cost from OpenRouter's cost_details (authoritative, includes search fees)
  const upstreamCostUsd = (lastUsageData as any)?.cost_details?.upstream_inference_cost
    || (lastUsageData as any)?.cost
    || undefined;

  const tokenUsage: TokenUsageMetadata = {
    inputTokens: lastUsageData?.prompt_tokens,
    outputTokens: lastUsageData?.completion_tokens,
    reasoningTokens,
    citationTokens,
    searchQueries,
    totalTokens: lastUsageData?.total_tokens,
    model,
    provider: 'perplexity',
    responseTimeMs,
    timestamp: new Date(),
    finishReason: lastFinishReason || undefined,
    upstreamCostUsd: typeof upstreamCostUsd === 'number' ? upstreamCostUsd : undefined,
  };

  // Log finish_reason for diagnostics (helps identify truncation vs voluntary stop)
  if (lastFinishReason) {
    console.log(`[Perplexity Stream] finish_reason: "${lastFinishReason}", completion_tokens: ${lastUsageData?.completion_tokens}, max_tokens_sent: ${params.maxTokens || 'not set'}`);
  }

  // Log raw usage object to diagnose token extraction (especially reasoning tokens)
  if (lastUsageData) {
    console.log('[Perplexity Stream] Raw usage object:', JSON.stringify(lastUsageData));
    console.log('[Perplexity Stream] Extracted tokens:', {
      reasoningTokens,
      citationTokens,
      searchQueries,
      upstreamCostUsd,
      inputTokens: lastUsageData?.prompt_tokens,
      outputTokens: lastUsageData?.completion_tokens,
      totalTokens: lastUsageData?.total_tokens,
    });
  }

  if (accumulatedCitations.length === 0) {
    console.warn('[Perplexity Stream] WARNING: No citations returned — possible grounding failure for model:', model);
  }

  params.onComplete?.(finalText, tokenUsage, accumulatedCitations);

  return { text: finalText, tokenUsage, citations: accumulatedCitations };
}

